import React, { useState, useRef, useCallback } from 'react'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { useTranslation } from 'react-i18next'
import { PhoneIcon } from './PhoneLink'
import { DollarIcon, CalendarIcon } from './Icons'

interface DealCustomer {
  id: number
  customer_id: number
  name: string
  email?: string
  phone?: string
  role: 'applicant' | 'co_applicant'
  order_position: number
}

interface Deal {
  id: number
  name: string
  title?: string
  amount: string
  customer_name: string
  customer_phone?: string
  customer_email?: string
  customer_id: number
  deal_type_id?: string
  type_name?: string
  commission?: {
    totalPoints: number
    totalPercentage: number
    grossCommission: number
    agencyFee: number
    closingFeeAmount: number
    netCommission: number
  }
  base_points: string
  bonus_points: string
  volume_points: string
  closing_date?: string
  expected_close_date?: string
  status_id: string
  status_name: string
  status_color: string
  status_is_archived?: boolean
  position: number
  description?: string
  created_at: string
  updated_at: string
  deal_customers?: DealCustomer[]
}

interface DealStatus {
  id: number
  name: string
  color: string
  position: number
  is_archived?: boolean
}

interface ColumnConfig {
  key: string
  name: string
  width?: string
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
  resizable?: boolean
}

interface ColumnWidth {
  [key: string]: number
}

interface DealsTableViewProps {
  statuses: DealStatus[]
  dealsByStatus: { [statusId: string]: Deal[] }
  onDragEnd: (result: DropResult) => void
  onDealClick: (deal: Deal) => void
  tableColumns: string[]
  className?: string
  showEmptyStatuses?: boolean
  onToggleEmptyStatuses?: () => void
}

const DealsTableView: React.FC<DealsTableViewProps> = ({
  statuses,
  dealsByStatus,
  onDragEnd,
  onDealClick,
  tableColumns,
  className = '',
  showEmptyStatuses = true,
  onToggleEmptyStatuses
}) => {
  const { t } = useTranslation()
  // Column width state management
  const [columnWidths, setColumnWidths] = useState<ColumnWidth>(() => {
    const savedWidths = localStorage.getItem('dealsTableColumnWidths')
    return savedWidths ? JSON.parse(savedWidths) : {}
  })
  
  // Resize state
  const [isResizing, setIsResizing] = useState(false)
  const [resizingColumn, setResizingColumn] = useState<string | null>(null)
  const resizeStartX = useRef<number>(0)
  const resizeStartWidth = useRef<number>(0)

  // Define column configurations for future customization
  const getColumnConfig = (columnKey: string): ColumnConfig => {
    const configs: Record<string, ColumnConfig> = {
      name: {
        key: 'name',
        name: t('deals.dealName'),
        width: 'min-w-48',
        align: 'left',
        sortable: true,
        resizable: true
      },
      customer_name: {
        key: 'customer_name',
        name: t('deals.customer'),
        width: 'min-w-64',
        align: 'left',
        sortable: true,
        resizable: true
      },
      amount: {
        key: 'amount',
        name: t('deals.amount'),
        width: 'min-w-32',
        align: 'right',
        sortable: true,
        resizable: true
      },
      deal_type: {
        key: 'deal_type',
        name: t('deals.type'),
        width: 'min-w-24',
        align: 'center',
        sortable: true,
        resizable: true
      },
      net_commission: {
        key: 'net_commission',
        name: t('deals.netCommission'),
        width: 'min-w-32',
        align: 'right',
        sortable: true,
        resizable: true
      },
      expected_close_date: {
        key: 'expected_close_date',
        name: t('deals.expectedClose'),
        width: 'min-w-32',
        align: 'left',
        sortable: true,
        resizable: true
      },
      status_name: {
        key: 'status_name',
        name: t('deals.status'),
        width: 'min-w-28',
        align: 'left',
        sortable: true,
        resizable: true
      },
      created_at: {
        key: 'created_at',
        name: t('deals.created'),
        width: 'min-w-28',
        align: 'left',
        sortable: true,
        resizable: true
      }
    }
    
    return configs[columnKey] || {
      key: columnKey,
      name: columnKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      width: 'min-w-32',
      align: 'left',
      sortable: false,
      resizable: true
    }
  }

  // Get default width for a column
  const getDefaultWidth = (columnKey: string): number => {
    const config = getColumnConfig(columnKey)
    if (config.width?.includes('min-w-')) {
      return parseInt(config.width.split('-')[2]) * 4
    }
    return 128 // Default width in pixels
  }

  // Get actual width for a column (custom or default)
  const getColumnWidth = (columnKey: string): number => {
    return columnWidths[columnKey] || getDefaultWidth(columnKey)
  }

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent, columnKey: string) => {
    e.preventDefault()
    e.stopPropagation()
    setIsResizing(true)
    setResizingColumn(columnKey)
    resizeStartX.current = e.clientX
    resizeStartWidth.current = getColumnWidth(columnKey)

    // Add global mouse event listeners
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStartX.current
      const newWidth = Math.max(80, resizeStartWidth.current + deltaX) // Minimum width of 80px
      
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: newWidth
      }))
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      setResizingColumn(null)
      
      // Save to localStorage
      const finalWidth = Math.max(80, resizeStartWidth.current + (window.event as MouseEvent).clientX - resizeStartX.current)
      const newWidths = {
        ...columnWidths,
        [columnKey]: finalWidth
      }
      setColumnWidths(newWidths)
      localStorage.setItem('dealsTableColumnWidths', JSON.stringify(newWidths))
      
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [columnWidths])

  // Get all deals in a flat array with status information
  const allDeals = statuses.flatMap(status => 
    (dealsByStatus[String(status.id)] || []).map(deal => ({
      ...deal,
      statusInfo: status
    }))
  )

  const formatCurrency = (amount: string | number) => {
    // Handle edge cases where amount might be an object or invalid value
    if (amount === null || amount === undefined || amount === '') {
      return '$0'
    }
    
    // If it's an object, try to extract a numeric value or default to 0
    if (typeof amount === 'object') {
      return '$0'
    }
    
    const numericAmount = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]+/g, '')) : amount
    if (isNaN(numericAmount) || numericAmount === 0) return '$0'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericAmount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getCellContent = (deal: Deal & { statusInfo: DealStatus }, column: string) => {
    switch (column) {
      case 'name':
        return (
          <span className="font-medium text-gray-900 dark:text-white truncate block">
            {deal.name || deal.title || 'Untitled Deal'}
          </span>
        )

      case 'customer_name':
        if (deal.deal_customers && deal.deal_customers.length > 0) {
          const applicant = deal.deal_customers.find(dc => dc.role === 'applicant')
          const coApplicants = deal.deal_customers.filter(dc => dc.role === 'co_applicant')
          
          return (
            <div className="flex items-center space-x-2 w-full">
              <div className="flex-1 min-w-0 overflow-hidden">
                {applicant && (
                  <div className="font-medium text-gray-900 dark:text-white truncate">
                    {applicant.name}
                  </div>
                )}
                {coApplicants.length > 0 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    + {coApplicants[0].name}
                    {coApplicants.length > 1 && ` & ${coApplicants.length - 1} more`}
                  </div>
                )}
              </div>
              {(applicant?.phone || deal.customer_phone) && (
                <PhoneIcon
                  phoneNumber={applicant?.phone || deal.customer_phone || ''}
                  customerId={applicant?.customer_id || deal.customer_id}
                  dealId={deal.id}
                  customerName={applicant?.name || deal.customer_name}
                  size={14}
                  className="flex-shrink-0"
                />
              )}
            </div>
          )
        }
        return (
          <div className="flex items-center space-x-2 w-full">
            <span className="font-medium text-gray-900 dark:text-white truncate flex-1 min-w-0">
              {deal.customer_name}
            </span>
            {deal.customer_phone && (
              <PhoneIcon
                phoneNumber={deal.customer_phone}
                customerId={deal.customer_id}
                dealId={deal.id}
                customerName={deal.customer_name}
                size={14}
                className="flex-shrink-0"
              />
            )}
          </div>
        )

      case 'amount':
        return (
          <div className="flex items-center text-green-600 dark:text-green-400 font-semibold justify-end">
            <DollarIcon size={14} className="mr-1 flex-shrink-0" />
            <span className="tabular-nums truncate">{formatCurrency(deal.amount)}</span>
          </div>
        )

      case 'deal_type':
        return deal.type_name ? (
          <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 truncate" style={{ maxWidth: '100%' }}>
            {deal.type_name}
          </span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">-</span>
        )

      case 'net_commission':
        return deal.commission && deal.commission.netCommission > 0 ? (
          <span className="font-medium text-green-600 dark:text-green-400 tabular-nums truncate block text-right">
            {formatCurrency(deal.commission.netCommission)}
          </span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500 text-right block">-</span>
        )

      case 'expected_close_date':
        return deal.expected_close_date ? (
          <div className="flex items-center text-gray-600 dark:text-gray-400 truncate">
            <CalendarIcon size={14} className="mr-1 flex-shrink-0" />
            <span className="truncate">{formatDate(deal.expected_close_date)}</span>
          </div>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">-</span>
        )

      case 'status_name':
        return (
          <div className="flex items-center space-x-2 truncate">
            <div 
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: deal.statusInfo.color }}
            />
            <span className="font-medium text-gray-900 dark:text-white truncate">
              {deal.status_name}
            </span>
          </div>
        )

      case 'created_at':
        return (
          <span className="text-gray-600 dark:text-gray-400 truncate block">
            {formatDate(deal.created_at)}
          </span>
        )

      default:
        return <span className="text-gray-400 dark:text-gray-500">-</span>
    }
  }

  const getColumnAlignment = (column: string) => {
    const config = getColumnConfig(column)
    switch (config.align) {
      case 'center': return 'text-center'
      case 'right': return 'text-right'
      default: return 'text-left'
    }
  }

  // Filter statuses based on showEmptyStatuses setting
  const visibleStatuses = showEmptyStatuses 
    ? statuses 
    : statuses.filter(status => (dealsByStatus[String(status.id)] || []).length > 0)

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden ${className}`}>
      {/* Header with toggle */}
      {onToggleEmptyStatuses && (
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              {t('deals.tableView')}
            </h3>
            <button
              onClick={onToggleEmptyStatuses}
              className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <span>{showEmptyStatuses ? t('deals.hide') : t('deals.show')} {t('deals.emptyStatuses')}</span>
              <div className={`w-3 h-3 border border-gray-400 rounded flex items-center justify-center ${showEmptyStatuses ? 'bg-gray-800 border-gray-900' : ''}`}>
                {showEmptyStatuses && (
                  <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </button>
          </div>
        </div>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="overflow-x-auto overflow-y-visible">
          <div className="min-w-full" style={{ width: 'max-content' }}>
            <table className="table-fixed" style={{ 
              width: `${tableColumns.reduce((total, column) => total + getColumnWidth(column), 0) + 96}px`,
              tableLayout: 'fixed'
            }}>
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                <tr>
                  {tableColumns.map((column) => {
                    const config = getColumnConfig(column)
                    const columnWidth = getColumnWidth(column)
                    return (
                      <th
                        key={column}
                        className={`px-6 py-3 ${getColumnAlignment(column)} text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider relative select-none`}
                        style={{ 
                          width: `${columnWidth}px`, 
                          minWidth: `${columnWidth}px`,
                          maxWidth: `${columnWidth}px`,
                          overflow: 'hidden'
                        }}
                      >
                        <div className="flex items-center space-x-1 whitespace-nowrap overflow-hidden w-full">
                          <span className="truncate">{config.name}</span>
                          {config.sortable && (
                            <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                            </svg>
                          )}
                        </div>
                        {config.resizable && (
                          <div
                            className="absolute top-0 right-0 w-1 h-full bg-transparent hover:bg-gray-800 cursor-col-resize z-20 group"
                            onMouseDown={(e) => handleResizeStart(e, column)}
                            style={{
                              background: isResizing && resizingColumn === column ? '#3B82F6' : 'transparent',
                            }}
                          >
                            <div className="w-1 h-full group-hover:bg-gray-800 transition-colors duration-150" />
                          </div>
                        )}
                      </th>
                    )
                  })}
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" style={{ width: '96px', minWidth: '96px' }}>
                    {t('deals.actions')}
                  </th>
                </tr>
              </thead>
            <tbody className="bg-white dark:bg-gray-800">
              {visibleStatuses.map((status) => {
                const statusDeals = dealsByStatus[String(status.id)] || []

                return (
                  <React.Fragment key={status.id}>
                    {/* Status header row */}
                    <tr className="bg-gray-100 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                      <td 
                        colSpan={tableColumns.length + 1}
                        className="px-6 py-3 text-sm font-semibold text-gray-900 dark:text-white"
                      >
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: status.color }}
                          />
                          <span>{status.name}</span>
                          <span className="text-gray-500 dark:text-gray-400">
                            ({statusDeals.length} {statusDeals.length !== 1 ? t('deals.dealCountPlural') : t('deals.dealCount')})
                          </span>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Draggable deals */}
                    <Droppable droppableId={status.id.toString()}>
                      {(provided, snapshot) => (
                        <React.Fragment>
                          <tr 
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`${snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                          >
                            <td colSpan={tableColumns.length + 1} className="p-0">
                              <div className={`${statusDeals.length === 0 ? 'min-h-[60px] flex items-center justify-center' : 'min-h-[2px]'}`}>
                                {statusDeals.length === 0 ? (
                                  <div className="text-gray-400 dark:text-gray-500 text-sm italic">
                                    {t('deals.noDealsInStatus')}
                                  </div>
                                ) : (
                                  statusDeals.map((deal, index) => (
                                  <Draggable key={deal.id} draggableId={deal.id.toString()} index={index}>
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`${
                                          snapshot.isDragging ? 'shadow-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-gray-900 rounded-lg mx-2' : ''
                                        }`}
                                      >
                                        <table className="table-fixed" style={{ 
                                          width: `${tableColumns.reduce((total, column) => total + getColumnWidth(column), 0) + 96}px`,
                                          tableLayout: 'fixed'
                                        }}>
                                          <tbody>
                                            <tr 
                                              onClick={() => onDealClick(deal)}
                                              className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                                            >
                                              {tableColumns.map((column) => {
                                                const config = getColumnConfig(column)
                                                const columnWidth = getColumnWidth(column)
                                                return (
                                                  <td 
                                                    key={column} 
                                                    className={`px-6 py-4 text-sm ${getColumnAlignment(column)}`}
                                                    style={{ 
                                                      width: `${columnWidth}px`, 
                                                      minWidth: `${columnWidth}px`,
                                                      maxWidth: `${columnWidth}px`,
                                                      overflow: 'hidden'
                                                    }}
                                                  >
                                                    <div className="overflow-hidden text-ellipsis whitespace-nowrap w-full">
                                                      {getCellContent({ ...deal, statusInfo: status }, column)}
                                                    </div>
                                                  </td>
                                                )
                                              })}
                                              <td className="px-6 py-4 text-right text-sm" style={{ width: '96px', minWidth: '96px' }}>
                                                <div className="flex items-center justify-end">
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation()
                                                      onDealClick(deal)
                                                    }}
                                                    className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 text-xs px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                    title={t('deals.editDeal')}
                                                  >
                                                    ✏️
                                                  </button>
                                                </div>
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </Draggable>
                                  ))
                                )}
                                {provided.placeholder}
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      )}
                    </Droppable>
                  </React.Fragment>
                )
              })}
            </tbody>
            </table>
          </div>
        </div>
      </DragDropContext>
    </div>
  )
}

export default DealsTableView